import { i18n } from "@tellescope/i18n";
import type { MongoFilter } from "./mongodb-filter";

/**
 * A single filter operation applied to one field. Shared across every consumer of the
 * reusable filter builder (calendar, contacts, tickets, contact-details blocks).
 */
export type FreeformOperation =
  | "equals"
  | "does_not_equal"
  | "is_set"
  | "is_not_set"
  | "contains"
  | "does_not_contain"
  | "before"
  | "after"
  | "less_than"
  | "greater_than"
  | "contains_any"
  | "contains_all"
  | "contains_none"
  | "is_between";

/**
 * The value type a field accepts, which drives which operations and which value input a
 * consumer renders for it.
 */
export type FreeformFieldValueType =
  | "string"
  | "number"
  | "date"
  | "tags"
  | "boolean"
  | "select"
  | "multi_select"
  | "person";

/** Inclusive ms-epoch bounds for an `is_between` condition; either bound may be open. */
export interface FreeformFilterRange {
  from?: number;
  to?: number;
}

/**
 * A single condition in a filter: a field, an operation, and (for value-bearing operations)
 * a value. `value` is a scalar for scalar fields, a string array for multi-valued fields and
 * membership operations, a boolean for boolean fields, and a {@link FreeformFilterRange} for
 * `is_between`.
 */
export interface FreeformFilter {
  /** Stable client key for the condition's editor row. Conditions saved before ids existed have none. */
  id?: string;
  field: string;
  operation: FreeformOperation;
  value?: string | number | boolean | string[] | FreeformFilterRange;
}

/**
 * A selectable field in the builder's field dropdown, describing how its value input behaves.
 * `operations`, when provided, restricts this field to exactly that set (subject to any
 * context-wide allow-list); when omitted, the field's operations default to its `valueType`.
 */
export interface FreeformFieldOption {
  value: string;
  label: string;
  valueType: FreeformFieldValueType;
  operations?: FreeformOperation[];
  /** The choices offered by `select`, `multi_select` and `tags` value inputs. */
  options?: { value: string; label: string }[];
  /** Lets the value input accept entries that are not among `options` (free-form tags). */
  creatable?: boolean;
  /** For `person` fields: which records the picker lists. */
  personModel?: "users" | "endusers";
}

export type FreeformFilterLogic = "and" | "or";

/**
 * A group of conditions combined under a single logic operator (all AND, or all OR).
 * `id` is a stable client key used for React reconciliation and block-level operations.
 */
export interface FreeformFilterBlock {
  id: string;
  logic: FreeformFilterLogic;
  filters: FreeformFilter[];
}

/** Operations that carry no value input (presence checks). */
export const NO_VALUE_OPERATIONS: FreeformOperation[] = ["is_set", "is_not_set"];

/** Operations valid for numeric comparison. */
export const NUMERIC_OPERATIONS: FreeformOperation[] = ["less_than", "greater_than"];

/** Operations valid for date comparison. */
export const DATE_OPERATIONS: FreeformOperation[] = ["before", "after"];

/** Operations valid for substring matching on strings. */
export const STRING_OPERATIONS: FreeformOperation[] = ["contains", "does_not_contain"];

/**
 * Returns the localized `{ value, label }` list of every operation, for the operation dropdown.
 */
export const getAllFreeformOperations = (): Array<{ value: FreeformOperation; label: string }> => [
  { value: "equals", label: i18n.t("equals") },
  { value: "does_not_equal", label: i18n.t("doesn't equal") },
  { value: "is_set", label: i18n.t("is set") },
  { value: "is_not_set", label: i18n.t("is not set") },
  { value: "contains", label: i18n.t("contains") },
  { value: "does_not_contain", label: i18n.t("does not contain") },
  { value: "before", label: i18n.t("before") },
  { value: "after", label: i18n.t("after") },
  { value: "less_than", label: i18n.t("less than") },
  { value: "greater_than", label: i18n.t("greater than") },
  { value: "contains_any", label: i18n.t("is any of") },
  { value: "contains_all", label: i18n.t("has all of") },
  { value: "contains_none", label: i18n.t("is none of") },
  { value: "is_between", label: i18n.t("is between") },
];

/**
 * Options for narrowing which operations a field may use.
 */
export interface FreeformOperationResolutionOptions {
  /**
   * The field's own explicit allow-list (from {@link FreeformFieldOption.operations}). When set,
   * the field is restricted to these operations (still intersected with `contextAllowed`).
   */
  fieldOperations?: FreeformOperation[];
  /**
   * A context-wide allow-list capping which operations any field may use (e.g. a consumer that
   * only permits equality). When set, the result is intersected with this list.
   */
  contextAllowed?: FreeformOperation[];
}

/** The operations offered by default for each value type. */
const OPERATIONS_BY_VALUE_TYPE: Record<FreeformFieldValueType, FreeformOperation[]> = {
  string: ["equals", "does_not_equal", "contains", "does_not_contain", "is_set", "is_not_set"],
  number: ["equals", "does_not_equal", "less_than", "greater_than", "is_set", "is_not_set"],
  date: ["equals", "does_not_equal", "before", "after", "is_set", "is_not_set", "is_between"],
  tags: ["equals", "does_not_equal", "contains", "does_not_contain", "contains_any", "contains_all", "contains_none"],
  boolean: ["equals"],
  select: ["equals", "does_not_equal", "is_set", "is_not_set", "contains_any", "contains_none"],
  multi_select: ["equals", "does_not_equal", "is_set", "is_not_set", "contains_any", "contains_all", "contains_none"],
  person: ["equals", "does_not_equal", "is_set", "is_not_set", "contains_any", "contains_all", "contains_none"],
};

/**
 * Returns the operations valid for a field of the given value type, then narrowed by the field's
 * own allow-list and/or a context-wide allow-list. Preserves the canonical operation ordering.
 */
export const getFreeformOperationsForValueType = (
  valueType: FreeformFieldValueType,
  options?: FreeformOperationResolutionOptions
): Array<{ value: FreeformOperation; label: string }> => {
  const forValueType = OPERATIONS_BY_VALUE_TYPE[valueType];
  const { fieldOperations, contextAllowed } = options ?? {};
  return getAllFreeformOperations().filter(
    ({ value }) =>
      forValueType.includes(value) &&
      (fieldOperations === undefined || fieldOperations.includes(value)) &&
      (contextAllowed === undefined || contextAllowed.includes(value))
  );
};

/**
 * True when a condition has everything it needs to compile (a field, and a value unless the
 * operation is value-less). Incomplete conditions are skipped during compilation.
 */
export const isFreeformFilterValueComplete = (filter: FreeformFilter): boolean => {
  if (!filter.field) return false;
  if (NO_VALUE_OPERATIONS.includes(filter.operation)) return true;
  const { value } = filter;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  if (isFreeformFilterRange(value)) return value.from !== undefined || value.to !== undefined;
  return value !== undefined && value !== "";
};

/** Narrows a condition value to the `is_between` range shape. */
export const isFreeformFilterRange = (value: FreeformFilter["value"]): value is FreeformFilterRange =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Escapes regex metacharacters so a user- or author-supplied string can be embedded in a `$regex`
 * condition as a literal rather than a pattern.
 */
export const escapeRegExp = (input: string): string => input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Options controlling how a condition compiles.
 */
export interface CompileFreeformFilterOptions {
  /**
   * How to interpret an array `value`. By default an array uses set-membership semantics against
   * the field ($in for equality, $nin for negation) — correct when matching a document field
   * against the whole selected set.
   *
   * When `true`, an array is instead treated as a list of per-item needles evaluated against a
   * single (scalar) field value: `equals`/`does_not_equal` keep $in/$nin, but `contains` matches if
   * the field contains ANY item (regex alternation) and `does_not_contain` matches if it contains
   * none. It also makes an AND block combine with an explicit `$and` array instead of merging into
   * one object, so multiple conditions on the SAME field don't clobber each other. Use this when
   * compiling a filter that will be evaluated against one item at a time (e.g. the contact-details
   * TagsBlock, which tests each tag individually). Off by default so existing consumers are
   * unaffected.
   */
  evalArraysByItem?: boolean;
  /**
   * Emits numeric (ms-epoch) values as `Date` for the value-comparing operations (`equals`,
   * `does_not_equal`, `before`, `after`, `is_between`). Use for fields the server stores as dates: a
   * `Date` serializes to an ISO string, which the API converts back to a date, whereas a number never
   * matches a date-typed field.
   */
  numericValuesAsDates?: boolean;
}

const asDate = <T>(value: T, options?: CompileFreeformFilterOptions): T | Date =>
  typeof value === "number" && options?.numericValuesAsDates ? new Date(value) : value;

const toArray = (value: FreeformFilter["value"]): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value === undefined || isFreeformFilterRange(value)) return [];
  return [value];
};

/**
 * Compiles a single complete condition into a MongoDB-style filter fragment. Tag-typed fields
 * use set-membership semantics ($in / $nin / $all). Callers should pre-filter with
 * {@link isFreeformFilterValueComplete}.
 */
export const compileFreeformFilter = (filter: FreeformFilter, options?: CompileFreeformFilterOptions): MongoFilter => {
  const { field, operation, value } = filter;

  if (operation === "contains_any") return { [field]: { $in: toArray(value) } };
  if (operation === "contains_all") return { [field]: { $all: toArray(value) } };
  if (operation === "contains_none") return { [field]: { $nin: toArray(value) } };
  if (operation === "is_between") {
    const range = isFreeformFilterRange(value) ? value : {};
    return {
      [field]: {
        ...(range.from !== undefined ? { $gte: asDate(range.from, options) } : {}),
        ...(range.to !== undefined ? { $lte: asDate(range.to, options) } : {}),
      },
    };
  }
  if (operation === "is_set") return { [field]: { $exists: true, $nin: [null, ""] } };
  if (operation === "is_not_set") return { [field]: { $in: [null, ""] } };

  if (Array.isArray(value)) {
    if (operation === "does_not_equal") return { [field]: { $nin: value } };
    if (options?.evalArraysByItem && (operation === "contains" || operation === "does_not_contain")) {
      const regex = { $regex: value.map(escapeRegExp).join("|"), $options: "i" };
      return { [field]: operation === "does_not_contain" ? { $not: regex } : regex };
    }
    return { [field]: { $in: value } };
  }

  if (operation === "equals") return { [field]: asDate(value, options) };
  if (operation === "does_not_equal") return { [field]: { $ne: asDate(value, options) } };
  if (operation === "contains") return { [field]: { $regex: value, $options: "i" } };
  if (operation === "does_not_contain") return { [field]: { $not: { $regex: value, $options: "i" } } };
  if (operation === "before") return { [field]: { $lt: asDate(value, options) } };
  if (operation === "less_than") return { [field]: { $lt: value } };
  if (operation === "after") return { [field]: { $gt: asDate(value, options) } };
  return { [field]: { $gt: value } };
};

/**
 * Compiles a whole block into a single MongoDB-style filter, combining its complete conditions
 * with the block's logic. Returns `undefined` when the block has no complete conditions.
 */
export const compileFreeformFilterBlock = (
  block: FreeformFilterBlock,
  options?: CompileFreeformFilterOptions
): MongoFilter | undefined => {
  const completeFilters = block.filters.filter(isFreeformFilterValueComplete);
  if (!completeFilters.length) return undefined;

  if (block.logic === "and") {
    // Multiple conditions on the SAME field can't be merged into one object (later keys clobber
    // earlier). When evaluating per item, conditions routinely repeat a field (e.g. tags equals X
    // AND tags contains Y), so combine with an explicit $and array. The default keeps the flat
    // Object.assign merge for backwards compatibility (existing consumers use distinct fields).
    if (options?.evalArraysByItem) {
      return { $and: completeFilters.map((filter) => compileFreeformFilter(filter, options)) };
    }
    return completeFilters.reduce<MongoFilter>(
      (acc, filter) => Object.assign(acc, compileFreeformFilter(filter, options)),
      {}
    );
  }

  return { $or: completeFilters.map((filter) => compileFreeformFilter(filter, options)) };
};

/**
 * Counts blocks that contribute at least one complete condition, for active-filter badges.
 */
export const countActiveFreeformFilterBlocks = (blocks: FreeformFilterBlock[] | undefined): number =>
  (blocks ?? []).filter((block) => block.filters.some(isFreeformFilterValueComplete)).length;
